import $ from 'jquery';
import { createPopper } from '@popperjs/core';
import { refreshScroll } from '../core/ScrollService.js';
import { initModules } from '../initModules.js';
import { initButtons } from '../features/buttons.js';
import Utils from '../utils/Utils.js';

export class HubPage {
    static matches() {
        return !!document.querySelector('[data-hub-root]');
    }

    init() {
        this.root = document.querySelector('[data-hub-root]');
        if (!this.root) return;

        this.filters = {
            theme: null,
            format: null,
            profil: null,
            scope: null,
            search: '',
        };
        this.paged = 1;
        this.scopeRequest = null;
        this.scopeApplyToken = 0;
        this.pendingScope = null;
        this.scopeCacheTimer = null;
        this.seoOutTimer = null;
        this.seoInTimer = null;
        this.scopeCache = new Map();
        this.filterPopper = null;

        this.readFiltersFromUrl();
        this.bindFilters();
        this.bindSearch();
        this.bindLoadMore();
        this.bindPopState();
        this.syncFilterPills();
        this.updateResetState();

        if (this.filters.search) {
            const $input = $(this.root).find('[data-hub-search] input[type="search"]');
            if ($input.length && !$input.val()) {
                $input.val(this.filters.search);
            }
            this.fetchSearch();
        }
    }

    readFiltersFromUrl() {
        const url = new URL(window.location.href);
        const path = url.pathname;
        const base = this.hubBasePath().replace(/^\/|\/$/g, '') || 'ressources';
        const baseRe = base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        this.filters.theme = url.searchParams.get('theme');
        this.filters.format = url.searchParams.get('format');
        this.filters.profil = url.searchParams.get('profil');
        this.filters.scope = url.searchParams.get('scope');
        this.filters.search = url.searchParams.get('search') || '';

        const themeMatch = path.match(new RegExp(`\\/${baseRe}\\/theme\\/([^/]+)`));
        const formatMatch = path.match(new RegExp(`\\/${baseRe}\\/format\\/([^/]+)`));
        const profilMatch = path.match(new RegExp(`\\/${baseRe}\\/profil\\/([^/]+)`));
        const scopeMatch = path.match(new RegExp(`\\/${baseRe}\\/(?:scope\\/)?(blog|boite-a-outils|evenements|communiques-de-presse)(?:\\/|$)`));

        if (themeMatch) this.filters.theme = themeMatch[1];
        if (formatMatch) this.filters.format = formatMatch[1];
        if (profilMatch) this.filters.profil = profilMatch[1];
        if (scopeMatch) this.filters.scope = scopeMatch[1];

        const isHubHomePath = new RegExp(`\\/${baseRe}\\/?$`).test(path)
            && !themeMatch && !formatMatch && !profilMatch && !scopeMatch
            && !url.searchParams.get('theme') && !url.searchParams.get('format') && !url.searchParams.get('profil') && !url.searchParams.get('scope');

        if (isHubHomePath) {
            this.filters.scope = null;
        } else if (!this.filters.scope) {
            if (this.root.hasAttribute('data-hub-list')) {
                this.filters.scope = this.root.getAttribute('data-hub-scope') || null;
                if (this.filters.scope === 'a-la-une') this.filters.scope = null;
            }
        }
    }

    currentScopeSlug() {
        if (this.pendingScope) return this.pendingScope;
        if (this.filters.scope) return this.filters.scope;
        if (this.root.hasAttribute('data-hub-home')) return 'a-la-une';
        return this.root.getAttribute('data-hub-scope') || 'a-la-une';
    }

    closeFilterSelects() {
        const $root = $(this.root);
        $root.find('.hubFilterBar .custom__select-content').each((_, el) => {
            el.style.display = 'none';
            el.removeAttribute('data-show');
        });
        $root.find('.hubFilterBar .custom__select-btn').removeClass('--opened');
        if (this.filterPopper) {
            this.filterPopper.destroy();
            this.filterPopper = null;
        }
    }

    positionFilterDropdown($select) {
        const btn = $select.find('.custom__select-btn')[0];
        const content = $select.find('.custom__select-content')[0];
        if (!btn || !content) return;

        if (this.filterPopper) {
            this.filterPopper.destroy();
            this.filterPopper = null;
        }

        content.style.display = 'block';
        content.setAttribute('data-show', 'true');

        this.filterPopper = createPopper(btn, content, {
            placement: 'bottom-start',
            strategy: 'fixed',
            modifiers: [
                {
                    name: 'offset',
                    options: { offset: [0, 8] },
                },
                {
                    name: 'flip',
                    options: {
                        fallbackPlacements: ['bottom-end', 'top-start', 'top-end', 'bottom', 'top'],
                    },
                },
                {
                    name: 'preventOverflow',
                    options: {
                        padding: 12,
                        boundary: 'viewport',
                        altAxis: true,
                    },
                },
                {
                    name: 'size',
                    enabled: true,
                    phase: 'beforeWrite',
                    requires: ['computeStyles'],
                    fn: ({ state }) => {
                        state.styles.popper.maxHeight = `${Math.min(Math.round(window.innerHeight * 0.55), 360)}px`;
                        state.styles.popper.minWidth = `${Math.max(state.rects.reference.width, 240)}px`;
                        state.styles.popper.maxWidth = `${Math.min(window.innerWidth - 24, 340)}px`;
                    },
                },
            ],
        });

        // Recalcul après paint (contenu visible)
        requestAnimationFrame(() => {
            if (this.filterPopper) this.filterPopper.update();
        });
    }

    bindFilters() {
        const $root = $(this.root);

        $root.on('click', '.hubFilterBar .custom__select-btn', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const $select = $(e.currentTarget).closest('.custom__select');
            const isOpen = $select.find('.custom__select-btn').hasClass('--opened');

            this.closeFilterSelects();

            if (!isOpen) {
                $select.find('.custom__select-btn').addClass('--opened');
                this.positionFilterDropdown($select);
            }
        });

        $(document).on('click.hubFilters', (e) => {
            if ($(e.target).closest('.hubFilterBar .custom__select, .hubFilterBar .custom__select-content').length) {
                return;
            }
            this.closeFilterSelects();
        });

        $(window).on('resize.hubFilters scroll.hubFilters', () => {
            if (this.filterPopper) {
                this.filterPopper.update();
            }
        });

        $root.on('click', '.hubFilterBar .custom__select-choice', (e) => {
            e.preventDefault();
            const $choice = $(e.currentTarget);
            const value = ($choice.attr('href') || '').replace('#', '');
            const term = $choice.closest('.custom__select').data('filter');
            const $select = $choice.closest('.custom__select');

            if (value === 'all') {
                this.filters[term] = null;
                $select.find('.custom__select-btn-text').text($select.data('text'));
                $select.find('.custom__select-btn').removeClass('--active');
                $select.find('li').removeClass('--disable');
            } else {
                this.filters[term] = value;
                $select.find('.custom__select-btn-text').text($choice.find('.choice__text').text());
                $select.find('.custom__select-btn').addClass('--active');
                $select.find('li').removeClass('--disable');
                $choice.closest('li').addClass('--disable');
            }

            this.closeFilterSelects();
            this.paged = 1;
            this.scopeCache.clear();
            this.fetchList();
        });

        // Reset filtres / recherche (pill ×, « Réinitialiser » barre ou panneau résultats)
        $root.on('click', '[data-hub-reset], [data-hub-pill-clear]', (e) => {
            e.preventDefault();
            const $target = $(e.currentTarget);
            const clearOnly = $target.attr('data-hub-pill-clear');

            if (clearOnly === 'search') {
                this.filters.search = '';
                $root.find('[data-hub-search] input[type="search"]').val('');
                this.paged = 1;
                this.scopeCache.clear();
                this.clearSearchPanel();
                this.syncFilterPills();
                if (this.root.hasAttribute('data-hub-home') || this.currentScopeSlug() === 'a-la-une') {
                    this.fetchScope('a-la-une', true);
                } else {
                    this.fetchList();
                }
                return;
            }

            this.filters.theme = null;
            this.filters.format = null;
            this.filters.profil = null;
            this.filters.search = '';
            this.paged = 1;
            this.scopeCache.clear();
            this.closeFilterSelects();

            $root.find('.hubFilterBar .custom__select').each(function () {
                const $select = $(this);
                $select.find('.custom__select-btn-text').text($select.data('text'));
                $select.find('.custom__select-btn').removeClass('--active');
                $select.find('li').removeClass('--disable');
            });
            $root.find('[data-hub-search] input[type="search"]').val('');

            this.clearSearchPanel();
            this.syncFilterPills();

            if (this.root.hasAttribute('data-hub-home') || this.currentScopeSlug() === 'a-la-une') {
                this.fetchScope('a-la-une', true);
            } else {
                this.fetchList();
            }
        });
    }

    bindSearch() {
        // Délégation : la barre filtres est remplacée en AJAX
        $(this.root).on('submit', '[data-hub-search]', (e) => {
            e.preventDefault();
            const value = ($(e.currentTarget).find('input[type="search"]').val() || '').trim();
            this.filters.search = value;
            this.paged = 1;

            if (!value) {
                this.clearSearchPanel();
                this.syncFilterPills();
                this.updateResetState();
                return;
            }

            this.scopeCache.clear();
            this.syncFilterPills();
            this.fetchSearch();
        });
    }

    bindLoadMore() {
        $(this.root).on('click', '[data-hub-load-more]', (e) => {
            e.preventDefault();
            this.paged += 1;
            this.fetchList(true);
        });

        $(this.root).on('click', '[data-hub-search-load-more]', (e) => {
            e.preventDefault();
            const $btn = $(e.currentTarget);
            const group = $btn.attr('data-group');
            const page = parseInt($btn.attr('data-page'), 10) + 1;
            if (!group || Number.isNaN(page)) return;
            this.fetchSearchMore(group, page, $btn);
        });
    }

    bindPopState() {
        window.addEventListener('popstate', () => {
            this.readFiltersFromUrl();
            if (this.filters.search) {
                this.fetchSearch();
                return;
            }
            this.clearSearchPanel();

            const path = window.location.pathname;
            const base = this.hubBasePath().replace(/^\/|\/$/g, '') || 'ressources';
            const baseRe = base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const scopeMatch = path.match(new RegExp(`\\/${baseRe}\\/(?:scope\\/)?(blog|boite-a-outils|evenements|communiques-de-presse)(?:\\/|$)`));
            const slug = scopeMatch
                ? scopeMatch[1]
                : (this.filters.scope || 'a-la-une');
            this.fetchScope(slug, false);
        });
    }

    clearSearchPanel() {
        $(this.root).find('[data-hub-search-results]').attr('hidden', true).empty();
        $(this.root).find('[data-hub-main]').show();
        this.syncFilterPills();
        refreshScroll(this.root);
    }

    hubBasePath() {
        const hubUrl = this.root.getAttribute('data-hub-url') || '/ressources/';
        try {
            const path = new URL(hubUrl, window.location.origin).pathname || '/ressources/';
            return path.replace(/\/?$/, '/');
        } catch (e) {
            return '/ressources/';
        }
    }

    buildListUrl() {
        const hubUrl = this.root.getAttribute('data-hub-url') || '/ressources/';
        const base = this.hubBasePath();
        const active = {};
        if (this.filters.theme) active.theme = this.filters.theme;
        if (this.filters.format) active.format = this.filters.format;
        if (this.filters.profil) active.profil = this.filters.profil;

        const keys = Object.keys(active);
        if (keys.length === 1) {
            const key = keys[0];
            return new URL(`${base}${key}/${active[key]}/`, window.location.origin).href;
        }
        if (keys.length > 1) {
            const url = new URL(hubUrl, window.location.origin);
            keys.forEach((k) => url.searchParams.set(k, active[k]));
            if (this.filters.scope) url.searchParams.set('scope', this.filters.scope);
            return url.href;
        }
        if (this.filters.scope) {
            return new URL(`${base}${this.filters.scope}/`, window.location.origin).href;
        }
        return hubUrl;
    }

    scopeCacheKey(scope) {
        return [
            scope || 'a-la-une',
            this.filters.theme || '',
            this.filters.format || '',
            this.filters.profil || '',
            this.filters.search || '',
        ].join('|');
    }

    scopeUrlHint(scope) {
        const hubUrl = this.root.getAttribute('data-hub-url') || '/ressources/';
        const base = this.hubBasePath();
        const theme = this.filters.theme;
        const format = this.filters.format;
        const profil = this.filters.profil;
        const active = {};
        if (theme) active.theme = theme;
        if (format) active.format = format;
        if (profil) active.profil = profil;
        const keys = Object.keys(active);
        const scopeForUrl = (!scope || scope === 'a-la-une') ? (keys.length ? 'blog' : null) : scope;

        if (keys.length === 1) {
            const key = keys[0];
            return new URL(`${base}${key}/${active[key]}/`, window.location.origin).href;
        }
        if (keys.length > 1) {
            const url = new URL(hubUrl, window.location.origin);
            keys.forEach((k) => url.searchParams.set(k, active[k]));
            if (scopeForUrl) url.searchParams.set('scope', scopeForUrl);
            return url.href;
        }
        if (scopeForUrl) {
            return new URL(`${base}${scopeForUrl}/`, window.location.origin).href;
        }
        return new URL(hubUrl, window.location.origin).href;
    }

    fetchScope(slug, push = true) {
        const scope = slug || 'a-la-une';
        const cacheKey = this.scopeCacheKey(scope);
        const token = ++this.scopeApplyToken;
        this.pendingScope = scope;

        // Annule toute appli différée / requête en cours (évite qu’un scope périmé écrase le hero)
        if (this.scopeCacheTimer) {
            window.clearTimeout(this.scopeCacheTimer);
            this.scopeCacheTimer = null;
        }
        if (this.scopeRequest && this.scopeRequest.abort) {
            this.scopeRequest.abort();
            this.scopeRequest = null;
        }

        if (push) {
            Utils.updateUrl(this.scopeUrlHint(scope));
        }

        const cached = this.scopeCache.get(cacheKey);
        if (cached) {
            this.setLoading(true);
            // Court flash loading même en cache pour transition lisible
            this.scopeCacheTimer = window.setTimeout(() => {
                this.scopeCacheTimer = null;
                if (token !== this.scopeApplyToken) return;
                this.applyScopeResponse(cached, false);
                this.setLoading(false);
            }, 120);
            return;
        }

        this.setLoading(true);

        const currentLayout = this.root.querySelector('[data-hub-filters]')?.getAttribute('data-hub-filter-layout') || '';
        const keepFilters = (scope === 'blog' || scope === 'boite-a-outils')
            && currentLayout === 'list'
            && !this.filters.search;

        this.scopeRequest = $.ajax({
            url: ajaxVars.url,
            type: 'GET',
            dataType: 'json',
            data: {
                action: 'ressources_scope_ajax',
                scope,
                theme: this.filters.theme || '',
                format: this.filters.format || '',
                profil: this.filters.profil || '',
                search: this.filters.search || '',
                omit_filters: keepFilters ? 1 : 0,
            },
            success: (response) => {
                if (token !== this.scopeApplyToken) return;
                if (!response || !response.success) return;
                this.scopeCache.set(cacheKey, response.data);
                this.applyScopeResponse(response.data, false);
            },
            complete: () => {
                if (token !== this.scopeApplyToken) return;
                this.scopeRequest = null;
                this.setLoading(false);
            },
        });
    }

    setLoading(on) {
        this.root.classList.toggle('is-loading', !!on);
        const el = this.root.querySelector('[data-hub-loading]');
        if (el) {
            el.setAttribute('aria-hidden', on ? 'false' : 'true');
            el.setAttribute('aria-busy', on ? 'true' : 'false');
        }
    }

    applyScopeResponse(data, push = true) {
        if (!data) return;

        const $root = $(this.root);
        const $filters = $root.find('[data-hub-filters]');
        const $main = $root.find('[data-hub-main]');
        const $hero = $root.find('.hubHero');

        // Figma événements / presse : pas de barre recherche — retirer / réinjecter selon le scope
        this.root.classList.toggle('hub--events', data.scope === 'evenements');
        if (data.filter_layout === 'none' || data.scope === 'evenements') {
            $filters.remove();
            $root.find('[data-hub-search-results]').remove();
            this.root.classList.add('hub--noFilters');
        } else if (data.html_filters) {
            this.root.classList.remove('hub--noFilters');
            if ($filters.length) {
                $filters.replaceWith(data.html_filters);
            } else if ($hero.length) {
                $hero.after(data.html_filters);
                if (!$root.find('[data-hub-search-results]').length) {
                    $root.find('[data-hub-filters]').after('<div class="hubSearchResults" data-hub-search-results hidden></div>');
                }
            }
        }
        if (data.html_main && $main.length) {
            $main.replaceWith(data.html_main);
            const mainEl = this.root.querySelector('[data-hub-main]');
            initModules();
            // Effet bulle hover (.button_circle) — initModules ne le fait pas
            initButtons(mainEl || this.root);
        }

        // Root mode
        if (data.root_mode === 'home') {
            this.root.removeAttribute('data-hub-list');
            this.root.removeAttribute('data-hub-events-list');
            this.root.setAttribute('data-hub-home', '');
            this.root.classList.remove('hub--list', 'hub--events', 'hub--noFilters');
            this.filters.scope = null;
        } else {
            this.root.removeAttribute('data-hub-home');
            this.root.setAttribute('data-hub-list', '');
            this.root.classList.add('hub--list');
            this.filters.scope = data.scope === 'a-la-une' ? null : data.scope;
            if (data.scope === 'evenements') {
                this.root.setAttribute('data-hub-events-list', '');
                this.filters.search = '';
                this.filters.theme = null;
                this.filters.format = null;
                this.filters.profil = null;
            } else {
                this.root.removeAttribute('data-hub-events-list');
            }
        }

        this.root.setAttribute('data-hub-scope', data.scope || 'a-la-une');
        if (data.tag_mode) {
            this.root.setAttribute('data-tag-mode', data.tag_mode);
        }

        this.pendingScope = null;
        this.applySeo(data.seo);
        this.updateFilterCounts(data.terms);
        this.syncFilterPills();
        this.updateResetState();

        if (push && data.url) {
            Utils.updateUrl(data.url);
        }

        refreshScroll(document.querySelector('[data-hub-main]'));
    }

    applySeo(seo) {
        if (!seo) return;

        if (this.seoOutTimer) {
            window.clearTimeout(this.seoOutTimer);
            this.seoOutTimer = null;
        }
        if (this.seoInTimer) {
            window.clearTimeout(this.seoInTimer);
            this.seoInTimer = null;
        }

        const $hero = $(this.root).find('.hubHero');
        const $contentBox = $hero.find('.hubHero__inner > .container').first();
        let $title = $hero.find('[data-hub-seo-title]');
        let $intro = $hero.find('[data-hub-seo-intro]');
        const $breadcrumb = $hero.find('[data-hub-seo-breadcrumb]');
        const nextTitle = seo.title || '';
        const nextIntro = typeof seo.intro !== 'undefined' ? (seo.intro || '') : null;
        const nextBreadcrumb = typeof seo.breadcrumb !== 'undefined' ? (seo.breadcrumb || '') : null;

        if (!$title.length && $contentBox.length) {
            $contentBox.prepend('<h1 class="st1 st1--light" data-hub-seo-title></h1>');
            $title = $hero.find('[data-hub-seo-title]');
        }
        if (!$intro.length && $contentBox.length) {
            $contentBox.append('<div class="header_content" data-hub-seo-intro></div>');
            $intro = $hero.find('[data-hub-seo-intro]');
        }

        // Toujours retirer les classes d’anim qui forcent opacity:0
        $hero.removeClass('is-seo-out is-seo-in');
        $title.add($intro).css({ opacity: '', transform: '', animation: '' });

        if ($title.length) {
            if (nextTitle) {
                $title.html(nextTitle).removeAttr('hidden');
                document.title = $('<div/>').html(nextTitle).text() + ' - Silae';
            } else {
                $title.empty().attr('hidden', true);
            }
        }
        if (nextIntro !== null && $intro.length) {
            if (nextIntro) {
                $intro.html(nextIntro).removeAttr('hidden');
            } else {
                $intro.empty().attr('hidden', true);
            }
        }
        if (nextBreadcrumb !== null && $breadcrumb.length) {
            $breadcrumb.html(nextBreadcrumb);
            if (nextBreadcrumb) {
                $breadcrumb.removeAttr('hidden');
                $hero.find('.hubHero__inner').addClass('has_breadcrumbs');
            } else {
                $breadcrumb.attr('hidden', true).empty();
                $hero.find('.hubHero__inner').removeClass('has_breadcrumbs');
            }
        }
    }

    fetchList(append = false, push = true) {
        // Sur hub home (ou sans zone résultats) : swap vers la liste via scope AJAX
        if (!this.root.hasAttribute('data-hub-list') || !$(this.root).find('[data-hub-results] .ajaxList_results').length) {
            const hasTax = !!(this.filters.theme || this.filters.format || this.filters.profil);
            const targetScope = this.filters.scope || (hasTax ? 'blog' : 'a-la-une');
            this.fetchScope(targetScope, push);
            return;
        }

        const $results = $(this.root).find('[data-hub-results] .ajaxList_results');
        if (!$results.length) return;

        if (!append) {
            $results.closest('.ajaxList').addClass('ajaxList--loading');
        } else {
            $(this.root).find('[data-hub-load-more]').addClass('loadMoreButton--loading');
        }

        $.ajax({
            url: ajaxVars.url,
            type: 'GET',
            dataType: 'json',
            data: {
                action: 'ressources_ajax',
                theme: this.filters.theme || '',
                format: this.filters.format || '',
                profil: this.filters.profil || '',
                scope: this.filters.scope || '',
                search: this.filters.search || '',
                paged: this.paged,
                append: append ? 1 : 0,
            },
            success: (response) => {
                if (!response || !response.success) return;
                const data = response.data;
                const $html = $(`<div>${data.html}</div>`);

                if (append) {
                    $(this.root).find('[data-hub-load-more]').closest('.hubList__more').remove();

                    const $incomingMonths = $html.find('.hubEventsList__month');
                    if ($incomingMonths.length) {
                        const $list = $results.find('.hubEventsList').first();
                        $incomingMonths.each((_, section) => {
                            const $section = $(section);
                            const key = $section.attr('data-month-key') || '';
                            const $existing = key
                                ? $list.find(`.hubEventsList__month[data-month-key="${key}"]`)
                                : $();
                            if ($existing.length) {
                                $existing.find('.hubEventsList__rows').append($section.find('.hubEvent'));
                            } else if ($list.length) {
                                $list.append($section);
                            } else {
                                $results.append($section);
                            }
                        });
                        const $more = $html.find('.hubList__more');
                        if ($more.length) {
                            ($list.length ? $list : $results).append($more);
                        }
                    } else {
                        const $grid = $results.find('.hubList__grid, .hubEventsList__rows').last();
                        if ($grid.length && $html.find('.hubList__grid, .hubEventsList__rows').length) {
                            $grid.append($html.find('.hubList__grid > *, .hubEventsList__rows > *'));
                            const $more = $html.find('.hubList__more');
                            if ($more.length) $results.append($more);
                        } else {
                            $results.append($html.children());
                        }
                    }
                } else {
                    $results.html(data.html);
                    $results.closest('.ajaxList').removeClass('ajaxList--loading');
                }

                this.applySeo(data.seo);
                this.updateFilterCounts(data.terms);
                this.updateResetState();

                if (push && data.url) {
                    Utils.updateUrl(data.url);
                }

                refreshScroll(document.querySelector('[data-hub-results] .ajaxList_results'));
                initButtons(this.root.querySelector('[data-hub-results]') || this.root);
            },
        });
    }

    getSearchScope() {
        let scope = 'hub';
        if (this.root.hasAttribute('data-hub-list')) {
            scope = this.filters.scope || this.root.getAttribute('data-hub-scope') || 'blog';
            if (scope === 'a-la-une') scope = 'hub';
        }
        return scope;
    }

    fetchSearch() {
        const $panel = $(this.root).find('[data-hub-search-results]');
        $panel.removeAttr('hidden').html('<div class="container"><div class="ajaxList ajaxList--loading"></div></div>');
        $(this.root).find('[data-hub-main]').hide();
        refreshScroll(this.root);

        const scope = this.getSearchScope();

        $.ajax({
            url: ajaxVars.url,
            type: 'GET',
            dataType: 'json',
            data: {
                action: 'ressources_search_ajax',
                search: this.filters.search,
                scope,
                page: 1,
            },
            success: (response) => {
                if (!response || !response.success) {
                    $panel.html('<div class="container"><div class="hubEmpty"><h2 class="hubEmpty__title">Aucun résultat pour votre recherche</h2></div></div>');
                    this.syncFilterPills();
                    this.updateResetState();
                    refreshScroll(this.root);
                    return;
                }
                $panel.html(response.data.html);
                const url = new URL(window.location.href);
                url.searchParams.set('search', this.filters.search);
                Utils.updateUrl(url.href);
                this.syncFilterPills();
                this.updateResetState();
                refreshScroll($panel[0] || this.root);
                initButtons($panel[0] || this.root);
            },
            error: () => {
                $panel.html('<div class="container"><div class="hubEmpty"><h2 class="hubEmpty__title">Aucun résultat pour votre recherche</h2></div></div>');
                this.syncFilterPills();
                this.updateResetState();
                refreshScroll(this.root);
            },
        });
    }

    fetchSearchMore(group, page, $btn) {
        if (!$btn || !$btn.length) return;
        $btn.addClass('loadMoreButton--loading');

        const label = $btn.find('span').text() || 'Charger d’autres résultats';
        const scope = this.getSearchScope();

        $.ajax({
            url: ajaxVars.url,
            type: 'GET',
            dataType: 'json',
            data: {
                action: 'ressources_search_ajax',
                search: this.filters.search,
                scope,
                group,
                page,
                append: 1,
            },
            success: (response) => {
                $btn.removeClass('loadMoreButton--loading');
                if (!response || !response.success || !response.data) return;

                const $group = $btn.closest('.hubSearch__group');
                const $list = $group.find('.hubList__grid, .hubEventsList__rows').first();
                if ($list.length && response.data.items_html) {
                    $list.append(response.data.items_html);
                }

                const $moreWrap = $btn.closest('.hubList__more');
                $moreWrap.remove();

                if (response.data.has_more) {
                    $group.append(
                        `<div class="hubList__more">
                            <button type="button" class="button loadMoreButton" data-hub-search-load-more data-group="${group}" data-page="${page}">
                                <span>${label}</span>
                            </button>
                        </div>`
                    );
                }

                refreshScroll($group[0] || this.root);
                initButtons($group[0] || this.root);
            },
            error: () => {
                $btn.removeClass('loadMoreButton--loading');
            },
        });
    }

    updateFilterCounts(terms) {
        if (!terms) return;
        Object.keys(terms).forEach((tax) => {
            (terms[tax] || []).forEach((item) => {
                const el = this.root.querySelector(`.custom__select[data-filter="${tax}"] .custom__select-choice[data-id="${item.id}"]`);
                if (!el) return;
                const countEl = el.querySelector('.choice__count');
                if (countEl) {
                    countEl.textContent = item.count ? `(${item.count})` : '';
                }
                el.classList.toggle('hidden', item.count === 0);
            });
        });
    }

    /**
     * #359 — affiche la pill query + reset sous la barre filtres
     * (y compris quand 0 résultat).
     */
    syncFilterPills() {
        const $root = $(this.root);
        let $meta = $root.find('[data-hub-filter-meta]');
        const $filters = $root.find('[data-hub-filters]');
        if (!$meta.length && $filters.length) {
            $filters.find('.container').first().append(
                `<div class="hubFilterBar__meta" data-hub-filter-meta hidden>
                    <hr class="hubFilterBar__divider" aria-hidden="true">
                    <div class="hubFilterBar__pills" data-hub-filter-pills></div>
                    <a href="#" class="hubFilterBar__reset" data-hub-reset>Réinitialiser</a>
                </div>`
            );
            $meta = $root.find('[data-hub-filter-meta]');
        }

        if (!$meta.length) return;

        const $pills = $meta.find('[data-hub-filter-pills]');
        const q = (this.filters.search || '').trim();
        $pills.empty();

        if (q) {
            const closeSrc = (typeof ajaxVars !== 'undefined' && ajaxVars.hub_pill_close)
                ? ajaxVars.hub_pill_close
                : '';
            const closeImg = closeSrc
                ? `<img class="hubFilterBar__pillClose" src="${closeSrc}" alt="" width="7" height="7" decoding="async">`
                : '';
            const safe = $('<div>').text(q).html();
            $pills.append(
                `<button type="button" class="hubFilterBar__pill" data-hub-pill-clear="search" aria-label="Retirer la recherche « ${safe} »">
                    <span class="hubFilterBar__pillLabel">${safe}</span>
                    ${closeImg}
                </button>`
            );
            $meta.removeAttr('hidden');
        } else {
            $meta.attr('hidden', true);
        }
    }

    updateResetState() {
        const canReset = !!(this.filters.theme || this.filters.format || this.filters.profil || this.filters.search);
        $(this.root).find('[data-hub-reset]')
            .toggleClass('is-disabled', !canReset)
            .toggleClass('--disabled', !canReset);
    }
}
